import React, {
    useEffect,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { filter } from 'rxjs/operators';
import _get from 'lodash/get';

import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';
import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';
import MediaPlaybackWrapper from '@mlb/react-patterns/components/MediaPlayback/mediaPlaybackWrapper.tsx';
import Button from '@mlb/react-patterns/components/Button/button.tsx';

import {
    normalizeMediaItem,
    getShareContentType,
} from 'bdvp-common/src/utils';
import { useMedia } from 'bdvp-common/src/hooks';
import ShareBtn from 'bdvp-common/src/components/ShareButton';
import { MetadataTwitterButton } from 'bdvp-common/src/components/Metadata';
import { Tracking } from 'bdvp-tracking';

import Wrapper from '../ModalViewWrapper';
import BodyComponent from '../ModalBody';
import FooterComponent from '../ModalFooter';

const Body = styled(BodyComponent)`
    display: flex;
    flex-direction: column;
`;

const Footer = styled(FooterComponent)`
    flex-direction: row;

    ${media.mdmin`
        justify-content: space-between;
    `}
`;

const PlaybackWrapper = styled(MediaPlaybackWrapper)`
    width: 100%;
    background-color: ${COLORS.mlbGrey7};

    video,
    .mlb-player-controls-loader--container,
    .mlb-player-controls--loader::after {
        background-color: ${COLORS.mlbGrey7} !important;
    }
`;

const ShareButton = styled(ShareBtn)`
    margin-right: 12px;
`;

const ViewReelsButton = styled(Button).attrs((({ label, onClick }) => ({
    size: 'md',
    label: label,
    styleName: 'link-blue',
    handlers: {
        onClick,
    },
})))`
    min-width: 174px;
    ${media.mdmin`
        min-width: 120px;
    `}
`;
const BodyShareContainer = styled.div`
    display: block;
    margin-top: 20px;
    text-align: center;
    ${media.mdmin`
        display: none;
    `}
`;

const FooterShareContainer = styled.div`
    display: none;
    ${media.mdmin`
        display: block;
    `}
`;

const Success = ({
    dictionary,
    playerConfig,
    mediaPlayback,
    lastModalNode,
    userId,
    history,
    closeReelCreationModal,
}) => {

    const isMobile = useMedia([`(min-width: ${BREAKPOINTS.md})`], [false], true);
    const mediaPlaybackRef = useRef(mediaPlayback);
    const canTrackVideoStart = useRef(true);

    useEffect(() => {

        mediaPlaybackRef.current = mediaPlayback;

    }, [mediaPlayback]);

    const slug = _get(mediaPlayback, 'slug', '');

    const ShareContainerChildren = (
        <>
            <ShareButton
                mediaId={_get(mediaPlayback, 'id', '')}
                onClick={() => {

                    Tracking.trackReelCreationShareClick(slug);

                }}
            />
            <MetadataTwitterButton
                {...!isMobile && { twitterButtonRef: lastModalNode }}
                mediaPlayback={mediaPlayback}
                dictionary={dictionary}
                onClick={() => {

                    Tracking.onShare({
                        type: 'Twitter',
                        slug,
                        title: _get(mediaPlayback, 'title', ''),
                        contentType: getShareContentType(mediaPlayback),
                    });

                }}
            />
        </>
    );

    return (
        <Wrapper>
            <Body>
                <PlaybackWrapper
                    playbackContainerId={'reel-player'}
                    autoPlay={_get(playerConfig, 'autoPlay', false)}
                    initConfig={_get(playerConfig, 'initConfig', {})}
                    mediaConfig={_get(playerConfig, 'mediaConfig', {})}
                    playerOptions={{
                        ..._get(playerConfig, 'playerOptions', {}),
                        metaData: normalizeMediaItem(mediaPlayback),
                    }}
                    handleObservable={(observationCenter) => {

                        observationCenter.playerSubject.pipe(filter((event) => {

                            return event.type === 'play';

                        })).subscribe(() => {

                            if(!canTrackVideoStart.current) {

                                return;

                            }

                            Tracking.trackVideoStart({
                                title: _get(mediaPlaybackRef, 'current.title', ''),
                                slug: _get(mediaPlaybackRef, 'current.slug', ''),
                                duration: _get(mediaPlaybackRef, 'current.feeds[0].duration', ''),
                            });
                            canTrackVideoStart.current = false;

                        });
                        observationCenter.playerSubject.pipe(filter((event) => {

                            return event.type === 'ended';

                        })).subscribe(() => {

                            const duration = _get(mediaPlaybackRef, 'current.feeds[0].duration', '');
                            const playtimeSeconds = duration && duration.split(':').reduce((acc, time) => isNaN(time) ? 0 : (60 * acc) + +time, 0) || 0;

                            Tracking.trackVideoComplete({
                                title: _get(mediaPlaybackRef, 'current.title', ''),
                                slug: _get(mediaPlaybackRef, 'current.slug', ''),
                                duration,
                                playtimeSeconds,
                            });
                            canTrackVideoStart.current = true;

                        });

                    }}
                />
                <BodyShareContainer>
                    {ShareContainerChildren}
                </BodyShareContainer>
            </Body>
            <Footer>
                <ViewReelsButton
                    {...isMobile && { ref: lastModalNode }}
                    label={_get(dictionary, 'view_your_reels', 'View Your Reels')}
                    onClick={() => {

                        Tracking.trackReelViewYourReelsClick();
                        history.push({ pathname: `/${userId}/reels` });
                        closeReelCreationModal();

                    }}
                />
                <FooterShareContainer>
                    {ShareContainerChildren}
                </FooterShareContainer>
            </Footer>
        </Wrapper>
    );

};

Success.propTypes = {
    dictionary: PropTypes.object.isRequired,
    playerConfig: PropTypes.object.isRequired,
    mediaPlayback: PropTypes.object.isRequired,
    lastModalNode: PropTypes.object.isRequired,
    closeReelCreationModal: PropTypes.func.isRequired,
};

export default Success;
